import React, { useEffect, useState, useRef } from "react";
import { Table, Button, Modal, Form, Alert, Container, Row, Col, Card, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaCog, FaUtensils, FaProjectDiagram, FaExchangeAlt } from "react-icons/fa";
import { Modal as AntModal } from "antd";
import { useTipoServiciosAlimentacion } from '../../../services/hooks/useTipoServiciosAlimentacion';

const GestionTipoServiciosAlimentacion = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));
  
  // Usar el hook para tipos de servicio alimentación
  const { 
    tipoServiciosAlimentacion, 
    loading, 
    fetchTipoServiciosAlimentacion, 
    createTipoServicioAlimentacion, 
    updateTipoServicioAlimentacion, 
    deleteTipoServicioAlimentacion,
    fetchUsosTipoServicioAlimentacion,
    unificarTipoServicioAlimentacion,
  } = useTipoServiciosAlimentacion();
  
  const [state, setState] = useState({
    searchTerm: "",
    tipoSeleccionado: null
  });
  const [showModal, setShowModal] = useState(false);
  const [editTipo, setEditTipo] = useState(null);
  const [descripcion, setDescripcion] = useState("");
  const [codigoTipoComida, setCodigoTipoComida] = useState("");
  const [esTransportado, setEsTransportado] = useState(false);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tipoToDelete, setTipoToDelete] = useState(null);
  const [showUsosModal, setShowUsosModal] = useState(false);
  const [usosData, setUsosData] = useState(null);
  const [loadingUsos, setLoadingUsos] = useState(false);
  const [idDestinoUnificar, setIdDestinoUnificar] = useState("");
  const [unificando, setUnificando] = useState(false);
  const hasFetched = useRef(false);

  // Cargar datos iniciales
  useEffect(() => {
    const loadInitialData = async () => {
      if (!hasFetched.current) {
        try {
          await fetchTipoServiciosAlimentacion();
          hasFetched.current = true;
        } catch (error) {
          console.error("Error al cargar datos iniciales:", error);
          toast.error("Error al cargar datos iniciales", {
            position: "bottom-right",
            autoClose: 2000
          });
        }
      }
    };

    loadInitialData();
  }, [fetchTipoServiciosAlimentacion]);

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleTipoSelect = (tipo) => {
    setState(prev => ({ 
      ...prev, 
      tipoSeleccionado: prev.tipoSeleccionado?.id === tipo.id ? null : tipo 
    }));
  };

  const handleShowModal = (tipo = null) => {
    setEditTipo(tipo);
    setDescripcion(tipo ? tipo.descripcion : "");
    setCodigoTipoComida(tipo?.codigo_tipo_comida_proveedor || "");
    setEsTransportado(Boolean(tipo?.es_transportado));
    setShowModal(true);
    setError("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditTipo(null);
    setDescripcion("");
    setCodigoTipoComida("");
    setEsTransportado(false);
    setError("");
  };

  const handleSave = async () => {
    if (!descripcion.trim()) {
      setError("La descripción es obligatoria");
      return;
    }

    const payload = {
      descripcion: descripcion.trim(),
      codigo_tipo_comida_proveedor: codigoTipoComida || null,
      es_transportado: esTransportado,
    };

    try {
      if (editTipo) {
        await updateTipoServicioAlimentacion(editTipo.id, payload);
        toast.success("Tipo de servicio actualizado con éxito", {
          position: "bottom-right",
          autoClose: 1000
        });
      } else {
        await createTipoServicioAlimentacion(payload);
        toast.success("Tipo de servicio creado con éxito", {
          position: "bottom-right",
          autoClose: 1000
        });
      }
      // Recargar datos después del guardado
      await fetchTipoServiciosAlimentacion();
      handleCloseModal();
    } catch (error) {
      console.error("Error al guardar:", error);
      setError("Error al guardar. Intente nuevamente.");
      toast.error("Error al guardar el tipo de servicio", {
        position: "bottom-right",
        autoClose: 2000
      });
    }
  };

  const handleDelete = (id) => {
    if (!id) {
      toast.error("Por favor seleccione un tipo de servicio para eliminar", {
        position: "bottom-right",
        autoClose: 2000
      });
      return;
    }

    setTipoToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!tipoToDelete) return;

    setShowDeleteModal(false);
    
    // Limpiar selección si es el elemento a eliminar
    if (state.tipoSeleccionado?.id === tipoToDelete) {
      setState(prev => ({ ...prev, tipoSeleccionado: null }));
    }
    
    try {
      await deleteTipoServicioAlimentacion(tipoToDelete);
      toast.success("Tipo de servicio eliminado con éxito", {
        position: "bottom-right",
        autoClose: 1000
      });
      // Recargar datos después de eliminar
      await fetchTipoServiciosAlimentacion();
    } catch (error) {
      console.error("Error al eliminar:", error);
      toast.error("Error al eliminar el tipo de servicio", {
        position: "bottom-right",
        autoClose: 2000
      });
    } finally {
      setTipoToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTipoToDelete(null);
  };

  const handleShowUsos = async (tipo, e) => {
    if (e) e.stopPropagation();
    setLoadingUsos(true);
    setShowUsosModal(true);
    setUsosData(null);
    setIdDestinoUnificar("");
    try {
      const data = await fetchUsosTipoServicioAlimentacion(tipo.id);
      setUsosData(data);
    } catch (err) {
      console.error("Error al cargar usos:", err);
      setShowUsosModal(false);
    } finally {
      setLoadingUsos(false);
    }
  };

  const handleCloseUsosModal = () => {
    setShowUsosModal(false);
    setUsosData(null);
    setIdDestinoUnificar("");
  };

  const handleConfirmarUnificar = () => {
    if (!usosData?.tipo?.id || !idDestinoUnificar) {
      toast.error("Seleccione el tipo de servicio destino (el que se mantiene)");
      return;
    }
    const destino = (tipoServiciosAlimentacion || []).find(
      (t) => t.id === parseInt(idDestinoUnificar, 10)
    );
    AntModal.confirm({
      title: "Confirmar unificación",
      content: (
        <div>
          <p>
            Se absorberá <strong>{usosData.tipo.descripcion}</strong> (id {usosData.tipo.id})
            dentro de <strong>{destino?.descripcion || idDestinoUnificar}</strong> (id {idDestinoUnificar}).
          </p>
          <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
            Se actualizarán turnos, precios por comedor, orígenes, estaciones offline,
            menús y auditorías relacionadas. El tipo origen quedará desactivado.
          </p>
        </div>
      ),
      okText: "Unificar",
      cancelText: "Cancelar",
      okType: "danger",
      onOk: async () => {
        setUnificando(true);
        try {
          await unificarTipoServicioAlimentacion(
            usosData.tipo.id,
            parseInt(idDestinoUnificar, 10)
          );
          handleCloseUsosModal();
        } finally {
          setUnificando(false);
        }
      },
    });
  };

  // Filtrar tipos usando directamente el array del hook
  const filteredTipos = (Array.isArray(tipoServiciosAlimentacion) ? tipoServiciosAlimentacion : [])
    .filter((tipo) =>
      tipo.descripcion?.toLowerCase().includes(state.searchTerm.toLowerCase())
    );

  const tiposDestinoUnificar = (Array.isArray(tipoServiciosAlimentacion) ? tipoServiciosAlimentacion : [])
    .filter((t) => t.id !== usosData?.tipo?.id);

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Badge moderno */
          .dh-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            padding: 8px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Loading spinner personalizado */
          .dh-spinner {
            display: inline-block;
            width: 2rem;
            height: 2rem;
            border: 0.25rem solid rgba(102, 126, 234, 0.2);
            border-radius: 50%;
            border-top-color: #667eea;
            animation: spin 1s ease-in-out infinite;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          /* Alert personalizado */
          .dh-alert {
            border-radius: 15px;
            padding: 1rem 1.5rem;
            border: none;
            margin-bottom: 1.5rem;
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
            color: #1e40af;
            border-left: 4px solid #3b82f6;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaUtensils className="me-3" />
                  Mantenimiento Tipo Servicios de Alimentación
                </h1>
                <p className="dh-main-subtitle">Gestión de tipos de servicios alimentarios del sistema</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge className="dh-badge">
                  Proyecto: {selectedProject?.nombre || "No seleccionado"}
                </Badge>
                <Button 
                  className="btn-dh-secondary d-flex align-items-center"
                  onClick={() => navigate("/home")}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </div>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <FaCog className="me-2" />
                      Tipos de Servicios de Alimentación
                    </h5>
                    <div className="d-flex gap-2">
                      <Button
                        className="btn-dh-primary d-flex align-items-center"
                        onClick={() => handleShowModal()}
                        title="Agregar nuevo tipo de servicio"
                      >
                        <FaPlus className="me-2" size={14} />
                        Nuevo
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={() => {
                          if (state.tipoSeleccionado) {
                            handleShowModal(state.tipoSeleccionado);
                          } else {
                            toast.error("Seleccione un tipo de servicio para editar", {
                              position: "bottom-right",
                              autoClose: 2000
                            });
                          }
                        }}
                        title="Editar tipo seleccionado"
                      >
                        <FaEdit className="me-2" size={14} />
                        Editar
                      </Button>
                      <Button
                        className="btn-dh-danger d-flex align-items-center"
                        onClick={() => handleDelete(state.tipoSeleccionado?.id)}
                        title="Eliminar tipo seleccionado"
                      >
                        <FaTrash className="me-2" size={14} />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  {/* Barra de búsqueda */}
                  <div className="mb-4">
                    <div className="position-relative" style={{ maxWidth: '500px' }}>
                      <Form.Control
                        type="text"
                        className="dh-form-control"
                        placeholder="Buscar tipo de servicio..."
                        value={state.searchTerm}
                        onChange={handleSearch}
                        style={{ paddingLeft: '3rem' }}
                      />
                      <FaSearch 
                        className="position-absolute" 
                        style={{ 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6b7280' 
                        }} 
                      />
                    </div>
                  </div>

                  {/* Tabla de datos */}
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="dh-spinner"></div>
                      <p className="mt-3 text-muted">Cargando tipos de servicios...</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="dh-enhanced-table">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}></th>
                            <th>ID</th>
                            <th>Descripción</th>
                            <th style={{ width: "90px" }}>Logiflex</th>
                            <th style={{ width: "120px" }}>Transportado</th>
                            <th style={{ width: "140px" }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTipos.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <div className="dh-alert dh-alert-info">
                                  <FaUtensils className="me-2" />
                                  No hay tipos de servicios registrados o no coinciden con la búsqueda
                                </div>
                              </td>
                            </tr>
                          ) : (
                            filteredTipos.map((tipo) => (
                              <tr 
                                key={tipo.id}
                                className={state.tipoSeleccionado?.id === tipo.id ? 'selected' : ''}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleTipoSelect(tipo)}
                              >
                                <td>
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={state.tipoSeleccionado?.id === tipo.id}
                                      onChange={() => {}}
                                    />
                                  </div>
                                </td>
                                <td style={{ fontWeight: '600', color: '#374151' }}>{tipo.id}</td>
                                <td>{tipo.descripcion}</td>
                                <td>
                                  <Badge bg="secondary">
                                    {tipo.codigo_tipo_comida_proveedor || "—"}
                                  </Badge>
                                </td>
                                <td>
                                  {tipo.es_transportado ? (
                                    <Badge bg="success">Sí</Badge>
                                  ) : (
                                    <Badge bg="light" text="dark">No</Badge>
                                  )}
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="d-flex align-items-center"
                                    onClick={(e) => handleShowUsos(tipo, e)}
                                    title="Ver en qué proyectos se usa / unificar"
                                  >
                                    <FaProjectDiagram className="me-1" size={12} />
                                    Usos
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: '0.5rem 0.5rem 0 0'
        }}>
          <Modal.Title>
            <FaUtensils className="me-2" />
            {editTipo ? "Editar" : "Nuevo"} Tipo Servicio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2rem' }}>
          {error && (
            <Alert variant="danger" className="dh-alert" style={{ 
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(248, 113, 113, 0.1) 100%)',
              color: '#dc2626',
              borderLeft: '4px solid #ef4444',
              borderRadius: '10px'
            }}>
              {error}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#374151' }}>
                Descripción del Tipo de Servicio
              </Form.Label>
              <Form.Control
                type="text"
                className="dh-form-control"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                required
                placeholder="Ingrese la descripción del tipo de servicio"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: '600', color: '#374151' }}>
                Código Logiflex (tipoComida)
              </Form.Label>
              <Form.Select
                className="dh-form-control"
                value={codigoTipoComida}
                onChange={(e) => setCodigoTipoComida(e.target.value)}
              >
                <option value="">— Sin homologar (usar descripción) —</option>
                <option value="D">D — Desayuno</option>
                <option value="A">A — Almuerzo</option>
                <option value="C">C — Cena</option>
                <option value="R">R — Refrigerio</option>
                <option value="O">O — Onda</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Check
                type="switch"
                id="es-transportado-switch"
                label="Transportado (envía transportado=true a Logiflex)"
                checked={esTransportado}
                onChange={(e) => setEsTransportado(e.target.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ padding: '1.5rem 2rem', borderTop: '1px solid #e5e7eb' }}>
          <Button 
            className="btn-dh-secondary"
            onClick={handleCloseModal}
          >
            Cancelar
          </Button>
          <Button 
            className="btn-dh-primary"
            onClick={handleSave}
          >
            <FaPlus className="me-2" size={14} />
            {editTipo ? "Actualizar" : "Guardar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showUsosModal}
        onHide={handleCloseUsosModal}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Modal.Title>
            <FaProjectDiagram className="me-2" />
            Usos del tipo de servicio
            {usosData?.tipo ? ` — ${usosData.tipo.descripcion}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "1.5rem 2rem" }}>
          {loadingUsos ? (
            <div className="text-center py-4">
              <div className="dh-spinner"></div>
              <p className="mt-3 text-muted mb-0">Consultando usos…</p>
            </div>
          ) : usosData ? (
            <>
              <div className="dh-alert dh-alert-info mb-3">
                Total de referencias: <strong>{usosData.total_referencias || 0}</strong>
                {" · "}
                Proyectos con uso: <strong>{(usosData.proyectos || []).length}</strong>
              </div>

              {(usosData.proyectos || []).length === 0 ? (
                <p className="text-muted">
                  Este tipo no está referenciado en turnos ni configuración por proyecto.
                  Puede unificarlo o eliminarlo con menor riesgo.
                </p>
              ) : (
                <div className="table-responsive mb-4">
                  <Table size="sm" bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Proyecto</th>
                        <th>Turnos</th>
                        <th>Precios comedor</th>
                        <th>Orígenes</th>
                        <th>Offline</th>
                        <th>Dup. auditoria</th>
                        <th>Menús/Prog.</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usosData.proyectos.map((p) => (
                        <tr key={p.proyecto_id}>
                          <td>{p.proyecto_nombre}</td>
                          <td>{p.turnos_cabecera}</td>
                          <td>{p.tipos_servicios_comedor}</td>
                          <td>{p.tipos_origen}</td>
                          <td>
                            {(p.estaciones_offline || 0) +
                              (p.estaciones_offline_servicios || 0)}
                          </td>
                          <td>{p.servicios_duplicados}</td>
                          <td>
                            {(p.menus_ciclicos || 0) + (p.programaciones_menu || 0)}
                          </td>
                          <td>
                            <strong>{p.total}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <Card className="border-0" style={{ background: "#f8fafc" }}>
                <Card.Body>
                  <h6 className="mb-3">
                    <FaExchangeAlt className="me-2" />
                    Unificar con otro tipo
                  </h6>
                  <p className="text-muted small">
                    Elija el tipo que se mantiene. El actual (
                    <strong>{usosData.tipo?.descripcion}</strong>) se absorberá y se desactivará.
                    Use esto para fusionar duplicados como &quot;1 ALMUERZO&quot; y &quot;ALMUERZO&quot;.
                  </p>
                  <Row className="g-2 align-items-end">
                    <Col md={8}>
                      <Form.Label className="dh-form-label">Tipo destino (se mantiene)</Form.Label>
                      <Form.Select
                        className="dh-form-control"
                        value={idDestinoUnificar}
                        onChange={(e) => setIdDestinoUnificar(e.target.value)}
                      >
                        <option value="">Seleccione tipo destino…</option>
                        {tiposDestinoUnificar.map((t) => (
                          <option key={t.id} value={t.id}>
                            [{t.id}] {t.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Button
                        className="btn-dh-primary w-100"
                        disabled={!idDestinoUnificar || unificando || loading}
                        onClick={handleConfirmarUnificar}
                      >
                        <FaExchangeAlt className="me-2" />
                        {unificando ? "Unificando…" : "Unificar"}
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          ) : (
            <p className="text-muted mb-0">No se pudo cargar la información de usos.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-dh-secondary" onClick={handleCloseUsosModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <AntModal
        title={
          <span style={{ color: '#dc2626', fontWeight: '600' }}>
            <FaTrash className="me-2" />
            Confirmar Eliminación
          </span>
        }
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        centered
        okButtonProps={{
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600'
          }
        }}
        cancelButtonProps={{
          style: {
            background: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            fontWeight: '600'
          }
        }}
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ color: '#374151', marginBottom: '0.5rem' }}>
            ¿Está seguro de que desea eliminar este tipo de servicio?
          </p>
          <p style={{ color: '#6b7280', marginBottom: '0', fontSize: '0.9rem' }}>
            Esta acción no se puede deshacer.
          </p>
        </div>
      </AntModal>
    </>
  );
};

export default GestionTipoServiciosAlimentacion;
